import {PageFragment, PageFragmentProps, PageFragmentState} from "@/shared/PageFragment";
import React, {createRef, ReactElement} from "react";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "@/shared/BaseApp";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {Button, Typography} from "@mui/material";
import {PAGE_FRAGMENT_WIDTH, PD_LG, PD_MD, PD_XXLG} from "@/shared/dimens";
import {ProviderSettings} from "./settings";

export class ProviderTourHelper {

  static show(): void {
    const id = BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK}, () =>
      <ProviderTourFragment finish={() => BaseApp.CONTEXT.hideDialog(id)}/>);
    this.setShown(true);
  }

  static isShown(): boolean {
    const onboarding = ProviderSettings.getInstance().onboarding;
    return onboarding.tourShown;
  }

  static async setShown(shown: boolean): Promise<void> {
    const onboarding = ProviderSettings.getInstance().onboarding;
    onboarding.tourShown = shown;
    await onboarding.save();
  }
}

type TourPage = {
  title: string,
  text: string,
  image: string,
}

const TOUR_PAGES: TourPage[] = [
  {
    title: "Welcome to myCare",
    text: "myCare is the leading app connecting dentists and patients with a seamless, easy-to-use interface.",
    image: "/images/tour/welcome.svg",
  },
  {
    title: "Appointment Scheduling",
    text: "Let patients schedule appointments directly from your profile on dentwyze.in",
    image: "/images/tour/calendar.svg",
  },
  {
    title: "Patient Messaging",
    text: "Send messages and reminders to patients with a familiar interface, for free.",
    image: "/images/tour/messages.svg",
  },
  {
    title: "Documents and Billing",
    text: "Your clinic's dental and billing records are always safe and accessible anywhere with cloud-based storage.",
    image: "/images/tour/storage.svg",
  },
  {
    title: "Online Help",
    text: "If you still have questions, visit our online help portal for solutions to common problems.",
    image: "/images/tour/onlinehelp.svg",
  },
];

type ProviderTourFragmentProps = PageFragmentProps & {
  finish: () => void,
}

type ProviderTourFragmentState = PageFragmentState & {
  pageIndex: number,
}

class ProviderTourFragment extends PageFragment<ProviderTourFragmentProps, ProviderTourFragmentState> {

  private readonly swiperRef = createRef<any | null>();

  protected onCreateState(props?: ProviderTourFragmentProps): ProviderTourFragmentState {
    return {...super.onCreateState(props), pageIndex: 0};
  }

  renderContent(): ReactElement {
    return <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "84dvw",
        maxWidth: PAGE_FRAGMENT_WIDTH,
        height: "92dvh"
      }}>
      <Swiper
        onSwiper={(swiper) => {
          //@ts-ignore
          this.swiperRef.current = swiper;
        }}
        onSlideChange={swiper => {
          this.setState({
            pageIndex: swiper.activeIndex,
          });
        }}
        style={{width: "100%", flexGrow: 1}}
        pagination={true}
        modules={[Pagination]}>
        {TOUR_PAGES.map(page => <SwiperSlide>
          <StyledBoxColumn style={{
            padding: `${PD_LG}px ${PD_XXLG}px`,
            height: "100%",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <img src={page.image} style={{
              width: "100%",
              maxWidth: 480,
              aspectRatio: 1,
            }}/>
            <StyledBoxColumn style={{height: 120}}>
              <Typography style={{textAlign: "center"}} variant="h5">{page.title}</Typography>
              <Typography style={{textAlign: "center"}}>{page.text}</Typography>
              <StyledSpan/>
            </StyledBoxColumn>
          </StyledBoxColumn>
        </SwiperSlide>)}
      </Swiper>
      <StyledBoxRow style={{padding: PD_MD}}>
        <Button
          onClick={() => {
            if (this.state.pageIndex < TOUR_PAGES.length - 1) {
              this.swiperRef.current.slideNext();
            } else {
              this.props.finish();
            }
          }}
          variant={"contained"} style={{width: "100%"}}>
          {this.state.pageIndex < TOUR_PAGES.length - 1 ? "Next" : "Finish"}
        </Button>
      </StyledBoxRow>
    </div>;
  }
}